import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";

// Same limits as the mobile app recorder
const VOICE_LIMIT_SECS = 5 * 60;
const MIN_DURATION_SECS = 0.5;

// Prefer AAC/mp4 so recordings also play on the iOS app; fall back to webm
const MIME_CANDIDATES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
];

const pickMimeType = () =>
  MIME_CANDIDATES.find((type) => window.MediaRecorder?.isTypeSupported?.(type)) ||
  "";

const formatTime = (secs) => {
  const s = Math.floor(secs);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const buildVoiceFile = (voice) => {
  const type = (voice.blob.type || "audio/webm").split(";")[0];
  const ext = type === "audio/mp4" ? "m4a" : type.split("/")[1] || "webm";
  return new File([voice.blob], `voice_${Date.now()}.${ext}`, { type });
};

const VoiceRecorder = ({
  voice,
  onChange,
  onError,
  disabled,
  onRecordingChange,
}) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const discardRef = useRef(false);

  const cleanup = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setRecording(false);
    setElapsed(0);
  };

  const stop = (discard = false) => {
    discardRef.current = discard;
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    } else {
      cleanup();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stop(true), []);

  useEffect(() => {
    if (elapsed >= VOICE_LIMIT_SECS) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed]);

  // Optional: lets a form hold its submit while a recording is in progress
  useEffect(() => {
    onRecordingChange?.(recording);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      onError?.("Voice recording is not supported in this browser");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      onError?.(
        err?.name === "NotAllowedError"
          ? "Microphone access denied. Allow it in the browser settings."
          : "Could not access the microphone"
      );
      return;
    }
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    streamRef.current = stream;
    recorderRef.current = recorder;
    chunksRef.current = [];
    discardRef.current = false;

    recorder.ondataavailable = (e) => {
      if (e.data?.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const duration = (Date.now() - startedAtRef.current) / 1000;
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || mimeType || "audio/webm",
      });
      cleanup();
      if (discardRef.current) return;
      if (duration < MIN_DURATION_SECS || !blob.size) {
        onError?.("Recording is too short");
        return;
      }
      onChange({
        blob,
        url: URL.createObjectURL(blob),
        duration: Math.round(Math.min(duration, VOICE_LIMIT_SECS)),
      });
    };

    startedAtRef.current = Date.now();
    recorder.start(250);
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(
      () => setElapsed((Date.now() - startedAtRef.current) / 1000),
      250
    );
  };

  const removeVoice = () => {
    if (voice?.url) URL.revokeObjectURL(voice.url);
    onChange(null);
  };

  if (recording) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title="Discard">
          <IconButton size="small" onClick={() => stop(true)}>
            <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#D32F2F",
            animation: "voicePulse 1s ease-in-out infinite",
            "@keyframes voicePulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
        <Typography sx={{ fontSize: "14px", minWidth: "110px" }}>
          Recording {formatTime(elapsed)} / {formatTime(VOICE_LIMIT_SECS)}
        </Typography>
        <Tooltip title="Stop">
          <IconButton size="small" onClick={() => stop()}>
            <StopIcon sx={{ color: "#D32F2F" }} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  if (voice) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <audio
          controls
          src={voice.url}
          style={{ height: "36px", maxWidth: "260px" }}
        />
        <Tooltip title="Remove voice message">
          <IconButton size="small" onClick={removeVoice} disabled={disabled}>
            <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Tooltip title="Record voice message">
      <span>
        <IconButton size="small" onClick={start} disabled={disabled}>
          <MicIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default VoiceRecorder;
